/**
 * Mobile menu v2: tab switching, sub-menu drill-down, and search trigger.
 */
/** @type {WeakMap<HTMLElement, () => void>} */
const mobileMenuPlaceholderTeardowns = new WeakMap();
class MobileMenuDrawer {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.bindEvents();
    this.syncInitialTabState();
    this.initSearchPlaceholderAnimation();
  }

  bindEvents() {
    this.root.querySelectorAll('[data-mobile-menu-tab]').forEach((tab) => {
      if (!(tab instanceof HTMLButtonElement)) return;
      tab.addEventListener('click', () => this.switchTab(tab));
    });

    this.root.querySelectorAll('[data-mobile-menu-open-sub]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.addEventListener('click', () => this.openSubmenu(button));
    });

    this.root.querySelectorAll('[data-mobile-menu-back]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.addEventListener('click', () => this.closeSubmenu(button));
    });

    const searchTrigger = this.root.querySelector('[data-mobile-menu-search]');
    if (searchTrigger) {
      searchTrigger.addEventListener('click', () => this.openSearch());
    }
  }

  syncInitialTabState() {
    const tabs = this.root.querySelectorAll('[data-mobile-menu-tab]');
    if (!tabs.length) return;

    const activeTab =
      this.root.querySelector('[data-mobile-menu-tab].is-active') ||
      this.root.querySelector('[data-mobile-menu-tab][aria-selected="true"]') ||
      tabs[0];

    if (activeTab instanceof HTMLButtonElement) {
      this.switchTab(activeTab);
    }
  }

  /** @param {HTMLButtonElement} tab */
  switchTab(tab) {
    const panelId = tab.dataset.mobileMenuTab;
    if (!panelId) return;

    this.root.querySelectorAll('[data-mobile-menu-tab]').forEach((el) => {
      const isActive = el === tab;
      el.classList.toggle('is-active', isActive);
      el.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.root.querySelectorAll('[data-mobile-menu-panel]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      const isActive = panel.dataset.mobileMenuPanel === panelId;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      panel.style.setProperty('display', isActive ? 'block' : 'none', 'important');

      if (!isActive) {
        this.resetPanelViews(panel);
      }
    });
  }

  /** @param {HTMLButtonElement} button */
  openSubmenu(button) {
    const panel = button.closest('[data-mobile-menu-panel]');
    if (!(panel instanceof HTMLElement)) return;

    const handle = button.dataset.categoryHandle;
    const mainView = panel.querySelector('[data-mobile-menu-view="main"]');
    const subView = panel.querySelector(`[data-mobile-menu-sub="${handle}"]`);

    if (!(mainView instanceof HTMLElement) || !(subView instanceof HTMLElement)) return;

    mainView.hidden = true;
    subView.hidden = false;
  }

  /** @param {HTMLButtonElement} button */
  closeSubmenu(button) {
    const panel = button.closest('[data-mobile-menu-panel]');
    if (!(panel instanceof HTMLElement)) return;
    this.resetPanelViews(panel);
  }

  /** @param {HTMLElement} panel */
  resetPanelViews(panel) {
    const mainView = panel.querySelector('[data-mobile-menu-view="main"]');
    panel.querySelectorAll('[data-mobile-menu-view="sub"]').forEach((sub) => {
      if (!(sub instanceof HTMLElement)) return;
      sub.hidden = true;
    });
    if (mainView instanceof HTMLElement) mainView.hidden = false;
  }

  openSearch() {
    const searchModal = document.querySelector('#search-modal');
    if (searchModal && 'showDialog' in searchModal && typeof searchModal.showDialog === 'function') {
      searchModal.showDialog({ immediate: true });
      return;
    }

    const searchButton = document.querySelector('search-button .search-modal__button');
    if (searchButton instanceof HTMLElement) searchButton.click();
  }
  initSearchPlaceholderAnimation() {
    const container = this.root.querySelector('[data-mobile-menu-search-placeholder]');
    if (!(container instanceof HTMLElement)) return;

    const existingTeardown = mobileMenuPlaceholderTeardowns.get(container);
    if (existingTeardown) existingTeardown();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const items = Array.from(
      container.querySelectorAll('.mobile-menu-v2__search-placeholder-item')
    ).filter((item) => item instanceof HTMLElement);

    if (items.length <= 1) return;

    const intervalSeconds = Number(container.dataset.placeholderInterval) || 2;

    let current = 0;
    /** @type {number | null} */
    let intervalId = null;
    // Assume visible until the observer reports otherwise (or when IntersectionObserver is unsupported).
    let inView = !('IntersectionObserver' in window);
    /** @type {IntersectionObserver | null} */
    let observer = null;

    /** @param {number} index */
    const showWord = (index) => {
      items.forEach((item, itemIndex) => {
        item.classList.toggle('is-active', itemIndex === index);
      });
    };

    const stop = () => {
      if (intervalId === null) return;
      window.clearInterval(intervalId);
      intervalId = null;
    };

    const start = () => {
      if (intervalId !== null) return;
      intervalId = window.setInterval(() => {
        current = (current + 1) % items.length;
        showWord(current);
      }, intervalSeconds * 1000);
    };

    // Rotate only while the placeholder is in the viewport (drawer open) and the tab is visible;
    // resumes from the current word.
    const updateState = () => {
      if (inView && !document.hidden) start();
      else stop();
    };

    showWord(0);

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        inView = entry.isIntersecting;
        updateState();
      });
      observer.observe(container);
    } else {
      updateState();
    }

    document.addEventListener('visibilitychange', updateState);

    mobileMenuPlaceholderTeardowns.set(container, () => {
      stop();
      if (observer) observer.disconnect();
      document.removeEventListener('visibilitychange', updateState);
      items.forEach((item) => item.classList.remove('is-active'));
    });
  }
}

function initMobileMenuDrawer() {
  document.querySelectorAll('[data-mobile-menu]').forEach((root) => {
    if (!(root instanceof HTMLElement)) return;
    if (root.dataset.mobileMenuInit === 'true') return;
    root.dataset.mobileMenuInit = 'true';
    new MobileMenuDrawer(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMobileMenuDrawer);
} else {
  initMobileMenuDrawer();
}

document.addEventListener('shopify:section:load', initMobileMenuDrawer);
